import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Helmet } from "react-helmet";
import { Container, Table } from "reactstrap";

import { Heading } from "components/Heading";
import { CustomModal } from "components/CustomModal";
import { CustomButton } from "components/CustomButton";
import { PageHeadings, PageTitles } from "../../constants";
import { licensePlansApi, saveLicensePlan } from "../../api/api.service";

/**
 * What each plan costs and how many teachers it covers.
 *
 * The number that matters here is the teacher places, because it is the only
 * figure in the catalog that decides what a school can actually do — a Gold
 * school with 40 places cannot invite a 41st teacher, and until now that number
 * only existed in a seed file.
 *
 * Saving does NOT edit the plan. It retires the current version and writes a new
 * one, so "what was Gold when this district signed" stays answerable — the
 * question every renewal conversation turns on. Schools already on a plan are
 * untouched either way: a licence copies the seat count at the moment of
 * purchase, precisely so a change here cannot move somebody's cap underneath
 * them. The screen says so, because an admin who believes otherwise will either
 * avoid a change they should make or make one they shouldn't.
 */

const money = (cents, currency) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD", maximumFractionDigits: 0 }).format(
    (cents || 0) / 100
  );

const emptyDraft = {
  tier: "SILVER",
  billingInterval: "YEARLY",
  name: "",
  priceCents: "",
  teacherSeatCount: "",
  description: "",
};

const LicensePlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRetired, setShowRetired] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await licensePlansApi(showRetired ? { includeRetired: "true" } : {});
      setPlans(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      toast.error(e?.message || "Could not load the plans.");
    } finally {
      setLoading(false);
    }
  }, [showRetired]);

  useEffect(() => {
    load();
  }, [load]);

  // The draft carries the plan's full identity. It used to omit billingInterval,
  // so the (disabled) interval dropdown showed "Yearly" for the monthly plan and
  // save() sent YEARLY — and the server changed the yearly plan instead. The
  // save below no longer reads identity from the draft at all, but the form
  // still has to show the truth about which plan is open.
  const openEdit = (plan) =>
    setDraft({
      tier: plan.tier,
      audience: plan.audience,
      billingInterval: plan.billingInterval,
      name: plan.name || "",
      priceCents: String(plan.priceCents ?? ""),
      // Empty means unlimited, which is a real option rather than a missing
      // value — Platinum is sold that way.
      teacherSeatCount: plan.teacherSeatCount == null ? "" : String(plan.teacherSeatCount),
      description: plan.description || "",
      original: plan,
    });

  const save = async () => {
    if (!draft) return;
    const price = Number(draft.priceCents);
    if (!draft.name.trim() || Number.isNaN(price) || price < 0) return;

    const seats = (draft.teacherSeatCount || "").trim();
    if (seats !== "" && (Number.isNaN(Number(seats)) || Number(seats) < 1)) return;

    setSaving(true);
    try {
      const original = draft.original;
      const tier = original ? original.tier : draft.tier;
      const individual = tier === "INDIVIDUAL";

      const res = await saveLicensePlan({
        // An edit names the exact row it changes. The server reads the plan's
        // tier, audience and interval off that row and rejects a request that
        // disagrees, so this can only ever change the plan that was clicked.
        ...(original ? { planId: original.id } : {}),
        // Identity comes from the row being edited, never from form state.
        tier,
        audience: original ? original.audience : individual ? "INDIVIDUAL" : "SCHOOL",
        billingInterval: original ? original.billingInterval : draft.billingInterval,
        name: draft.name.trim(),
        priceCents: price,
        // An individual plan covers one person and has no seat count.
        teacherSeatCount: individual ? null : seats === "" ? null : Number(seats),
        description: draft.description.trim() || undefined,
      });
      toast.success(res?.changed === false ? "Nothing changed." : `${draft.name} updated for new purchases.`);
      setDraft(null);
      load();
    } catch (e) {
      toast.error(e?.message || "Could not save that plan.");
    } finally {
      setSaving(false);
    }
  };

  // An individual plan is sold to one person, so it has no teacher places and
  // must carry audience INDIVIDUAL. Derived rather than stored, so the form
  // cannot get out of step with the tier that is actually selected.
  const isIndividual = draft?.tier === "INDIVIDUAL";

  return (
    <div className="page-content">
      <Helmet>
        <title>{PageTitles.LICENSE_PLANS}</title>
      </Helmet>
      <Container fluid>
        <Heading heading={PageHeadings.LICENSE_PLANS} />
        <p className="text-muted mb-4">
          What each plan costs and how many teachers it covers. Changes apply to <strong>new purchases and
          renewals</strong> — a school already on a plan keeps the number of places it bought, so nobody loses
          seats because the catalogue moved. Saving keeps the old version on record rather than overwriting it.
        </p>

        <div className="d-flex align-items-center gap-2 mb-3">
          <CustomButton
            title={showRetired ? "Hide past versions" : "Show past versions"}
            color="light"
            onClick={() => setShowRetired((v) => !v)}
          />
          <div className="ms-auto">
            <CustomButton title="Add a plan" color="primary" onClick={() => setDraft({ ...emptyDraft })} />
          </div>
        </div>

        <div className="table-responsive">
          <Table borderless className="mb-0">
            <thead>
              <tr>
                <th>Plan</th>
                <th style={{ width: 150 }}>Teacher places</th>
                <th style={{ width: 130 }}>Price</th>
                <th style={{ width: 130 }}>Schools on it</th>
                <th style={{ width: 160 }}>Status</th>
                <th style={{ width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="text-muted">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && plans.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-muted">
                    No plans yet.
                  </td>
                </tr>
              )}
              {!loading &&
                plans.map((plan) => (
                  <tr key={plan.id}>
                    <td>
                      <div>{plan.name}</div>
                      <div className="text-muted font-size-12">{plan.tier}</div>
                    </td>
                    <td>
                      {plan.teacherSeatCount == null ? (
                        <span className="cs-badge cs-badge-active">Unlimited</span>
                      ) : (
                        <strong>{plan.teacherSeatCount}</strong>
                      )}
                    </td>
                    <td>
                      {money(plan.priceCents, plan.currency)}
                      <div className="text-muted font-size-12">
                        {plan.billingInterval === "MONTHLY" ? "per month" : "per year"}
                      </div>
                    </td>
                    <td>{plan.licensesOnThisVersion}</td>
                    <td>
                      {plan.isRetired ? (
                        <span className="cs-badge cs-badge-blocked">Past version</span>
                      ) : (
                        <span className="cs-badge cs-badge-active">Selling now</span>
                      )}
                    </td>
                    <td>
                      {/* A retired version is a record, not something to edit —
                          changing it would rewrite what a school was sold. */}
                      {!plan.isRetired && <CustomButton title="Edit" color="light" onClick={() => openEdit(plan)} />}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>

        <CustomModal isOpen={!!draft} onClose={() => !saving && setDraft(null)} toggle={() => !saving && setDraft(null)} size="md">
          {draft && (
            <div className="px-3 pb-3">
              <h5 className="mb-3">
                {draft.original
                  ? `Edit ${draft.original.name} (${draft.original.billingInterval === "MONTHLY" ? "per month" : "per year"})`
                  : "Add a plan"}
              </h5>

              <label className="form-label font-size-13">Tier</label>
              <select
                className="form-select mb-3"
                value={draft.tier}
                disabled={!!draft.original}
                onChange={(e) => setDraft({ ...draft, tier: e.target.value })}
              >
                <option value="SILVER">Silver</option>
                <option value="GOLD">Gold</option>
                <option value="PLATINUM">Platinum</option>
                {/* The individual plan is what a teacher buys for their own
                    classroom. It was missing here, so the one price on the
                    teacher paywall could not be changed from the console at
                    all. */}
                <option value="INDIVIDUAL">Individual</option>
              </select>

              {isIndividual ? (
                <div className="text-muted font-size-12 mb-3">
                  Sold to one teacher, for their own classroom. It has no teacher places.
                </div>
              ) : null}

              <label className="form-label font-size-13">Billing interval</label>
              <select
                className="form-select mb-3"
                value={draft.billingInterval}
                disabled={!!draft.original}
                onChange={(e) => setDraft({ ...draft, billingInterval: e.target.value })}
              >
                <option value="YEARLY">Yearly</option>
                <option value="MONTHLY">Monthly</option>
              </select>

              <label className="form-label font-size-13">Name shown to schools</label>
              <input
                className="form-control mb-3"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />

              {/* An individual plan covers exactly one person, so a seat count
                  on it is meaningless — and a number typed here would be
                  written to a column nothing reads. */}
              {isIndividual ? null : (
                <>
                  <label className="form-label font-size-13">Teacher places</label>
                  <input
                    className="form-control"
                    type="number"
                    min={1}
                    placeholder="Leave empty for unlimited"
                    value={draft.teacherSeatCount}
                    onChange={(e) => setDraft({ ...draft, teacherSeatCount: e.target.value })}
                  />
                  <div className="text-muted font-size-12 mt-1 mb-3">
                    How many teachers a school on this plan can invite. Leave it empty for unlimited.
                  </div>
                </>
              )}

              <label className="form-label font-size-13">Price, in cents</label>
              <input
                className="form-control"
                type="number"
                min={0}
                value={draft.priceCents}
                onChange={(e) => setDraft({ ...draft, priceCents: e.target.value })}
              />
              <div className="text-muted font-size-12 mt-1 mb-3">
                {draft.priceCents && !Number.isNaN(Number(draft.priceCents))
                  ? `That is ${money(Number(draft.priceCents), "USD")}.`
                  : "110000 is $1,100."}
              </div>

              <p className="text-muted font-size-12">
                {draft.original?.licensesOnThisVersion
                  ? `${draft.original.licensesOnThisVersion} school(s) are on this version. They keep what they bought — this only changes what new purchases and renewals get.`
                  : "No school is on this version yet."}
              </p>

              <div className="d-flex justify-content-end gap-2">
                <CustomButton title="Cancel" color="light" disabled={saving} onClick={() => setDraft(null)} />
                <CustomButton
                  title="Save"
                  color="primary"
                  loading={saving}
                  disabled={!draft.name.trim() || draft.priceCents === ""}
                  onClick={save}
                />
              </div>
            </div>
          )}
        </CustomModal>
      </Container>
    </div>
  );
};

export default LicensePlans;
